import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import MetricCard, { MetricItem } from 'components/FinancialCheckup/MetricCard/MetricCard';
import { getFinancialMetricsState } from 'selectors/financialMetrics';
import { getApplicationData } from 'selectors/getApplicationData';
import { getFinancialMetrics } from 'thunks';
import { PeriodMetrics } from 'api/FinancialMetricsApi';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import {
  getIncomeVariabilityStatus,
  getExpenseVariabilityStatus,
} from 'components/FinancialCheckup/FinancialDashboard/utils';

import styles from './FinancialMetricsSection.module.scss';

const createMetricItems = (metrics: PeriodMetrics | null): MetricItem[] | null => {
  if (!metrics || !metrics.mtdValue || !metrics.ytdValue) return null;
  return [
    {
      label: 'Month to date',
      value: formatMonetaryAmount(metrics.mtdValue, true),
    },
    {
      label: 'Year to date',
      value: formatMonetaryAmount(metrics.ytdValue, true),
    },
  ];
};

const FinancialMetricsSection: React.FC = () => {
  const dispatch = useDispatch();
  const { data: financialMetricsData } = useSelector(getFinancialMetricsState);
  const { application } = useSelector(getApplicationData);
  const applicationId = application?.id;

  useEffect(() => {
    if (applicationId) {
      dispatch(getFinancialMetrics(applicationId));
    }
  }, [dispatch, applicationId]);

  if (!financialMetricsData) return null;

  const { income, spending, ivi, evi } = financialMetricsData;

  const incomeItems = createMetricItems(income);
  const expenseItems = createMetricItems(spending);

  const incomeVariabilityStatus = typeof ivi === 'number' ? getIncomeVariabilityStatus(ivi) : undefined;
  const expenseVariabilityStatus = typeof evi === 'number' ? getExpenseVariabilityStatus(evi) : undefined;

  return (
    <>
      {incomeItems && (
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Income</p>
          <MetricCard items={incomeItems} variabilityStatus={incomeVariabilityStatus} />
        </div>
      )}
      {expenseItems && (
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Expenses</p>
          <MetricCard items={expenseItems} variabilityStatus={expenseVariabilityStatus} />
        </div>
      )}
    </>
  );
};

export default FinancialMetricsSection;
